// src/hooks/useEntityDetail.ts
//
// Fetches the entity detail payload (place info + AI summary) from the
// `entityDetail` Cloud Function when the user taps a chip in chat.
// Results are cached in memory for the session so tapping the same
// chip repeatedly doesn't re-hit the backend.

import { useEffect, useMemo, useRef, useState } from 'react';
import { auth } from '../services/firebase';
import { useAuthStore } from '../stores/authStore';
import { getLanguageHint } from '../utils/getLanguageHint';

const ENTITY_DETAIL_URL =
  'https://us-central1-travelx-46b7a.cloudfunctions.net/entityDetail';

export interface EntityPhoto {
  url: string;
  attribution?: string;
}

export interface PlaceReview {
  author_name: string;
  /** ISO 8601 timestamp from Google's `time` field. Newer = more recent. */
  published_at: string;
  /** Google's natural-language relative ("3 weeks ago"). */
  relative_time?: string;
  rating: number;
  text: string;
  author_photo_url?: string;
  language?: string;
}

export interface EntityDetailPayload {
  name: string;
  category: string;
  place_id?: string;
  address?: string;
  phone?: string;
  website?: string;
  rating?: number;
  user_ratings_total?: number;
  price_level?: number;
  hours?: string[];
  open_now?: boolean;
  location?: { lat: number; lng: number };
  types?: string[];
  photos?: EntityPhoto[];
  /** Sorted newest-first by Google's `time`, max 5. */
  reviews?: PlaceReview[];
  editorial_summary?: string;
  ai_summary: string;
  action_urls: {
    directions?: string;
    call?: string;
    website?: string;
    maps_open?: string;
  };
  source: 'places_with_ai' | 'ai_only';
  generated_at: number;
}

interface RequestParams {
  name: string;
  category: string;
  context?: string;
  location?: { lat: number; lng: number };
}

// Module-level cache. Key is `${category}:${normalized_name}:${langSlug}`.
// Language is part of the key so the same place looked up by users
// with different language preferences gets distinct cached entries
// rather than serving the first-cached language to everyone.
const detailCache = new Map<string, EntityDetailPayload>();
const inFlight = new Map<string, Promise<EntityDetailPayload>>();

function makeKey(category: string, name: string, langSlug: string): string {
  const norm = name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return `${category}:${norm}:${langSlug}`;
}

export function useEntityDetail(params: RequestParams | null) {
  const profile = useAuthStore((s) => s.profile);
  // Stable language hint + slug for cache keying. The hint is sent
  // to the backend; the slug separates cache entries between
  // different language preferences.
  const languageHint = useMemo(() => getLanguageHint(profile), [profile]);
  const languageSlug = useMemo(
    () =>
      (languageHint || 'default')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .slice(0, 16),
    [languageHint]
  );

  const [detail, setDetail] = useState<EntityDetailPayload | null>(() => {
    if (!params) return null;
    return (
      detailCache.get(makeKey(params.category, params.name, languageSlug)) ||
      null
    );
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancelRef = useRef(false);

  useEffect(() => {
    cancelRef.current = false;
    setError(null);

    if (!params) {
      setDetail(null);
      setLoading(false);
      return () => {
        cancelRef.current = true;
      };
    }

    const key = makeKey(params.category, params.name, languageSlug);
    const cached = detailCache.get(key);
    if (cached) {
      setDetail(cached);
      setLoading(false);
      return () => {
        cancelRef.current = true;
      };
    }

    setLoading(true);

    let promise = inFlight.get(key);
    if (!promise) {
      promise = (async () => {
        const user = auth.currentUser;
        if (!user) throw new Error('Not authenticated');
        const token = await user.getIdToken();
        const resp = await fetch(ENTITY_DETAIL_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          // language hint forwarded to the cloud function so the
          // AI summary lands in the user's language. Server uses
          // it as a fallback for users without a profile language.
          body: JSON.stringify({ ...params, language: languageHint }),
        });
        if (!resp.ok) {
          const text = await resp.text().catch(() => '');
          throw new Error(
            `entityDetail ${resp.status}: ${text.slice(0, 200) || resp.statusText}`
          );
        }
        const data = (await resp.json()) as EntityDetailPayload;
        detailCache.set(key, data);
        inFlight.delete(key);
        return data;
      })();
      // Make sure a failed promise still clears the inFlight slot
      promise.catch(() => inFlight.delete(key));
      inFlight.set(key, promise);
    }

    promise
      .then((d) => {
        if (!cancelRef.current) {
          setDetail(d);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelRef.current) {
          console.warn('entityDetail fetch failed:', err);
          setError(err.message || 'Failed to load details');
          setLoading(false);
        }
      });

    return () => {
      cancelRef.current = true;
    };
  }, [params?.name, params?.category, params?.location?.lat, params?.location?.lng, languageSlug, languageHint]); // eslint-disable-line react-hooks/exhaustive-deps

  return { detail, loading, error };
}
